import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, MailCheck, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      toast.error("Could not send a reset link right now. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F9FF] bg-polka flex items-center justify-center p-5">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", bounce: 0.5, duration: 0.6 }}
        className="w-full max-w-md"
      >
        {sent ? (
          <div className="card-soft text-center space-y-4" data-testid="forgot-password-sent">
            <div className="inline-flex w-20 h-20 rounded-3xl bg-[#4D96FF] items-center justify-center shadow-[0_8px_0_#1D4ED8] mb-2">
              <MailCheck className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
            <h1 className="font-display text-3xl font-bold text-[#1E1B4B]">Check your email!</h1>
            <p className="text-slate-600 font-semibold">
              If <span className="text-[#1E1B4B]">{email}</span> has a BuzzSpell account,
              we just sent it a link to reset the password.
            </p>
            <p className="text-sm text-slate-500">The link works for 1 hour.</p>
            <Link to="/login" className="btn-clay w-full inline-flex" data-testid="forgot-password-back-to-login">
              Back to login
            </Link>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex w-20 h-20 rounded-3xl bg-[#FBBF24] items-center justify-center shadow-[0_8px_0_#B45309] mb-4">
                <KeyRound className="w-10 h-10 text-[#1E1B4B]" strokeWidth={3} />
              </div>
              <h1 className="font-display text-4xl font-bold text-[#1E1B4B]">Forgot password?</h1>
              <p className="text-lg text-slate-600 mt-1 font-semibold">We'll email you a reset link</p>
            </div>

            <form onSubmit={onSubmit} className="card-soft space-y-4" data-testid="forgot-password-form">
              <div>
                <label className="block text-sm font-bold text-[#1E1B4B] mb-1.5 ml-2">Email</label>
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-2xl border-4 border-blue-100 focus:border-[#4D96FF] px-5 py-4 text-lg outline-none transition-all font-semibold"
                  placeholder="you@school.com"
                  data-testid="forgot-password-email-input"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-clay w-full" data-testid="forgot-password-submit-btn">
                <Send className="w-5 h-5" strokeWidth={3} /> {loading ? "Sending…" : "Send reset link"}
              </button>
              <p className="text-center text-sm text-slate-600 pt-2">
                Remembered it?{" "}
                <Link to="/login" className="font-bold text-[#4D96FF] hover:underline" data-testid="forgot-password-login-link">
                  Log in
                </Link>
              </p>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
